import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Video, GraduationCap, Hourglass, ShieldX, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { saveGuestSession } from "@/lib/guestApi";

const publicApi = axios.create({ baseURL: API });

/** Public, no-login-required registration page for a Cloudivo Meet the host has
 * opted into "Require Registration" for — Zoom-style webinar signup. Submits
 * name/email/phone + any host-defined custom fields, then either gets an
 * immediate guest access token or polls for host approval. */
export default function MeetingRegisterPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [meetingInfo, setMeetingInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", custom_fields: {} });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // null | pending | denied
  const pollRef = useRef(null);

  useEffect(() => {
    publicApi.get(`/meetings/public/${code}`).then(({ data }) => setMeetingInfo(data)).catch((e) => {
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "This meeting link is invalid.");
    });
  }, [code]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const pollStatus = (regId) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await publicApi.get(`/meetings/public/registrations/${regId}/status`);
        if (data.status === "approved") {
          clearInterval(pollRef.current);
          saveGuestSession(data.meeting_id, data.guest_token, form.name);
          navigate(`/meeting/guest/${data.meeting_id}`);
        } else if (data.status === "denied") {
          clearInterval(pollRef.current);
          setStatus("denied");
        }
      } catch { /* keep polling — a transient network hiccup shouldn't stop the wait screen */ }
    }, 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await publicApi.post(`/meetings/public/${code}/register`, form);
      if (data.status === "approved") {
        saveGuestSession(data.meeting_id, data.guest_token, form.name);
        navigate(`/meeting/guest/${data.meeting_id}`);
      } else {
        setStatus("pending");
        pollStatus(data.registration_id);
      }
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center gap-4 text-center px-6" data-testid="registration-load-error">
        <ShieldX className="w-10 h-10 text-red-400" />
        <p className="text-slate-300">{loadError}</p>
      </div>
    );
  }
  if (!meetingInfo) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  }

  if (status === "pending") {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center gap-4 text-center px-6" data-testid="registration-pending-screen">
        <Hourglass className="w-10 h-10 text-amber-400 animate-pulse" />
        <p className="text-white font-semibold">Waiting for {meetingInfo.host_name} to approve your registration...</p>
        <p className="text-sm text-slate-400">This page will move on automatically once you're approved.</p>
      </div>
    );
  }
  if (status === "denied") {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center gap-4 text-center px-6" data-testid="registration-denied-screen">
        <ShieldX className="w-10 h-10 text-red-400" />
        <p className="text-white font-semibold">Your registration wasn't approved for this meeting.</p>
      </div>
    );
  }

  if (!meetingInfo.registration_required) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center gap-4 text-center px-6" data-testid="registration-not-required-screen">
        <CheckCircle2 className="w-10 h-10 text-cyan-400" />
        <p className="text-white font-semibold">'{meetingInfo.title}' doesn't require registration.</p>
        <p className="text-sm text-slate-400">Log in to Cloudivo and use the meeting code to join directly.</p>
        <button onClick={() => navigate("/login")} data-testid="registration-go-to-login-btn" className="text-cyan-400 hover:text-cyan-300 text-sm font-semibold">Go to Login</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 py-10" data-testid="meeting-register-page">
      <div className="glass-panel rounded-2xl p-8 max-w-md w-full">
        <div className="flex items-center gap-2 mb-1 text-cyan-400">
          {meetingInfo.meeting_type === "class" ? <GraduationCap className="w-5 h-5" /> : <Video className="w-5 h-5" />}
          <span className="text-xs font-semibold uppercase tracking-wide">Registration</span>
        </div>
        <h1 className="font-heading text-2xl font-bold text-white mb-1">{meetingInfo.title}</h1>
        <p className="text-sm text-slate-400 mb-6">Hosted by {meetingInfo.host_name}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Full Name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="registration-name-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Email</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="registration-email-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Phone Number</label>
            <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="registration-phone-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
          </div>
          {(meetingInfo.registration_fields || []).map((f) => (
            <div key={f.key}>
              <label className="text-xs text-slate-400 mb-1 block">{f.label}{f.required && " *"}</label>
              <input required={f.required} value={form.custom_fields[f.key] || ""}
                onChange={(e) => setForm({ ...form, custom_fields: { ...form.custom_fields, [f.key]: e.target.value } })}
                data-testid={`registration-custom-field-${f.key}`} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
            </div>
          ))}
          <button type="submit" disabled={submitting} data-testid="registration-submit-btn"
            className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-lg py-2.5 text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Register &amp; Continue
          </button>
        </form>
      </div>
    </div>
  );
}
